import React, { useCallback } from 'react';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';

export function useTelemetry() {
  const appInsights = useAppInsightsContext();

  const trackEvent = React.useCallback(
    (eventName, customProperties = {}, measurements = undefined) => {
      if (appInsights) {
        const name =
          typeof eventName === 'object' &&
          eventName !== null &&
          typeof eventName.name === 'string'
            ? eventName.name
            : String(eventName);

        appInsights.trackEvent({ name: name }, customProperties, measurements);
      }
    },
    [appInsights]
  );

  const trackException = React.useCallback(
    (error, customProperties = {}, severityLevel = SeverityLevel.Error) => {
      if (appInsights) {
        const exception =
          error instanceof Error
            ? error
            : new Error(String(error ?? 'Unknown error'));
        const properties =
          typeof customProperties === 'object' && customProperties !== null
            ? customProperties
            : {};

        appInsights.trackException({
          exception: exception,
          properties: properties,
          severityLevel: severityLevel
        });
      }
    },
    [appInsights]
  );

  const createTrackEventHandler = useCallback(
    (defaultProperties = {}) => {
      return (eventName, extendedProperties = {}, measurements = undefined) => {
        const mergedProperties = {
          ...defaultProperties,
          ...extendedProperties
        };
        trackEvent(eventName, mergedProperties, measurements);
      };
    },
    [trackEvent]
  );

  const createTrackExceptionHandler = useCallback(
    (defaultProperties = {}) => {
      return (
        error,
        extendedProperties = {},
        severityLevel = SeverityLevel.Error
      ) => {
        const mergedProperties = {
          ...defaultProperties,
          ...extendedProperties
        };
        trackException(error, mergedProperties, severityLevel);
      };
    },
    [trackException]
  );

  return {
    trackEvent,
    trackException,
    createTrackEventHandler,
    createTrackExceptionHandler,
    isAvailable: !!appInsights
  };
}

export { SeverityLevel };
